import { STATUS_TRANSACTION, TYPE_TRANSACTION } from '../constants/dashboard.constants';
import type {
  DashboardFilterCatalogs,
  DashboardFilters,
  GenericFilter,
  MovementCatalogValues,
} from '../types/dashboard.types';

/** Tope de días hacia atrás que acepta el backend (y que valida el FE). */
export const MAX_RANGE_DAYS = 180;

/** Zona horaria de negocio (hora CDMX, sin DST desde 2022). */
const CDMX_OFFSET = '-06:00';

const toISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Primer día seleccionable: hoy − 180 días.
 */
export function getMinSelectableDate(today: Date = new Date()): Date {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  base.setDate(base.getDate() - MAX_RANGE_DAYS);
  return base;
}

const toStartOfDayISO = (isoDate: string): string => `${isoDate}T00:00:00.000${CDMX_OFFSET}`;
const toEndOfDayISO = (isoDate: string): string => `${isoDate}T23:59:59.999${CDMX_OFFSET}`;

/**
 * Rango efectivo que se consulta: el elegido o, por defecto, los últimos 180 días.
 */
export function getEffectiveRange(
  filters: DashboardFilters,
  today: Date = new Date()
): { from: string; to: string } {
  if (filters.from && filters.to) {
    return { from: filters.from, to: filters.to };
  }
  return { from: toISODate(getMinSelectableDate(today)), to: toISODate(today) };
}

/**
 * Mapea los filtros de la UI al `genericFilters` que espera `space-account-list-tx`.
 *
 * Contrato verificado en el BE (`space_account-space-list-tx`): es un arreglo de
 * objetos de una sola llave, con fechas ISO 8601 en hora CDMX.
 * Las llaves `email`, `card`, `status` y `type` corresponden a la extensión de
 * BF-615 (correo parcial, tarjeta por últimos 4, estado y tipo).
 */
export function buildGenericFilters(filters: DashboardFilters): GenericFilter[] {
  const genericFilters: GenericFilter[] = [];

  if (filters.from) genericFilters.push({ start_date: toStartOfDayISO(filters.from) });
  if (filters.to) genericFilters.push({ end_date: toEndOfDayISO(filters.to) });

  const email = filters.email?.trim();
  if (email) genericFilters.push({ email });

  const card = filters.cardLast4?.trim();
  if (card) genericFilters.push({ card });

  if (filters.status) genericFilters.push({ status: filters.status });
  if (filters.type) genericFilters.push({ type: filters.type });

  return genericFilters;
}

/**
 * ¿Hay algún criterio cargado? Controla el estado del botón "Aplicar filtros".
 */
export function hasAnyFilter(filters: DashboardFilters): boolean {
  return Boolean(
    filters.from ||
      filters.to ||
      filters.status ||
      filters.type ||
      filters.email?.trim() ||
      filters.cardLast4?.trim()
  );
}

/**
 * Convierte los valores crudos del catálogo del BE en opciones para los
 * selects, traduciendo los códigos conocidos con los mapas de la tabla.
 */
export function buildMovementFilterCatalogs(
  catalogs?: MovementCatalogValues
): DashboardFilterCatalogs {
  return {
    cards: [],
    statuses: (catalogs?.statuses ?? []).map((value) => ({
      value,
      label: STATUS_TRANSACTION[value] ?? value,
    })),
    types: (catalogs?.types ?? []).map((value) => ({
      value,
      label: TYPE_TRANSACTION[value] ?? value,
    })),
  };
}
